import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import callApi from '../services/callApi.tsx'
import { WebThemaDto } from '../types/webTypes/WebThemaDto.tsx'
import { FetchApiDataOptions } from '../types/reduxTypes.tsx'

export interface BookedResponse {
  data: WebThemaDto
}

interface Error {
  message: string
}

interface BookingState {
  webTopic: WebThemaDto | null
  loading: 'idle' | 'pending' | 'succeeded' | 'failed'
  error: string | null
  statusCode: number | null
}

const initialState: BookingState = {
  webTopic: null,
  loading: 'idle',
  error: null,
  statusCode: null,
}

async function fetchApiData({ url, token, method, responseType, bodyData }: FetchApiDataOptions): Promise<BookedResponse | Error> {
  try {
    const response = await callApi({ url, token, method, responseType, bodyData })
    if (response.status === 200) {
      return { data: response.data }
    } else {
      throw new Error('Nicht erfolgreicher Statuscode')
    }
  } catch (error) {
    if (error instanceof Error) {
      return { message: error.message }
    }
    return { message: 'Unbekannter Fehler' }
  }
}

const apiAction = (actionName: string) =>
  createAsyncThunk<BookedResponse, FetchApiDataOptions, { rejectValue: Error }>(actionName, async (options, { rejectWithValue }) => {
    const result = await fetchApiData(options)
    if ('data' in result) {
      return result
    } else {
      return rejectWithValue(result)
    }
  })

// Fetch event web data
export const fetchWebEventDataFromApi = apiAction('api/fetchWebEventDataFromApi')

// Fetch hotels
export const fetchHotelsFromApi = apiAction('api/fetchHotelsFromApi')

const bookingSlice = createSlice({
  name: 'bookingStore',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // Fetch: Web Event Data
      .addCase(fetchWebEventDataFromApi.pending, (state) => {
        state.loading = 'pending'
      })
      .addCase(fetchWebEventDataFromApi.fulfilled, (state, action) => {
        const result = action.payload.data as WebThemaDto
        if (result) state.webTopic = result
      })
      .addCase(fetchWebEventDataFromApi.rejected, (state, action) => {
        state.loading = 'failed'
        state.error = action.error.message || 'An error occurred'
      })
  },
})

export const {} = bookingSlice.actions

export default bookingSlice.reducer
